import React, { useState, useEffect } from "react";
import { viewRecord } from "../../../../firebase/db/shares/crud.tsx";
import { useParams } from "react-router-dom";
import { Audio } from "react-loader-spinner";
import WavesurferPlayer from "@wavesurfer/react";
import WaveSurfer from "wavesurfer.js";

interface SongShareType {
	audio_url?: string;
	id?: string;
	comments: string;
	created?: Timestamp;
	css_class?: string;
	plays: number;
	song_name?: string;
	wave_color?: string;
	user_id?: string;
}

const SongShare = () => {
	const { id } = useParams();
	const [configData, setConfigData] = useState<SongShareType | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState("");
	const [themeClass, setThemeClass] = useState("form-body-classic");
	const [songName, setSongName] = useState("");
	const [shareComment, setShareComment] = useState(String);
	const [isWaveLoading, setIsWaveLoading] = useState<Boolean>(true);
	const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);

	useEffect(() => {
		const fetchDataAndSetState = async () => {
			try {
				console.log("Fetching data...");
				if (id) {
					const fetchedData = await viewRecord(id);
					if (fetchedData !== null) {
						setConfigData(fetchedData);
						setThemeClass(fetchedData.css_class);
						setIsLoading(false);
					} else {
						setErrorMessage("Error loading configurations");
					}
				} else {
					setErrorMessage("Invalid ID");
				}
			} catch (e) {
				setErrorMessage(String(e)); //More informative error message
			} finally {
				setIsLoading(false);
			}
		};
		fetchDataAndSetState();
	}, [id]);

	const onReady = (ws) => {
		setIsWaveLoading(false);
		setWavesurfer(ws);
		setIsPlaying(false);
		setIsWaveLoading(false);
	};

	const onPlayPause = () => {
		wavesurfer?.playPause();
	};

	if (isLoading) {
		return (
			<Audio
				height="150"
				width="150"
				color="#00c951"
				ariaLabel="audio-loading"
				wrapperStyle={{}}
				wrapperClass="wrapper-class h-screen top-auto w-full justify-center content-center flex self-center place-content-center place-items-center"
				visible={true}
			/>
		);
	} else if (configData) {
		return (
			<div className={`h-screen ${themeClass}`}>
				<main className="h-screen top-auto w-full justify-center content-center flex self-center place-content-center place-items-center">
					<div className="form-panel w-96 space-y-5 p-8 border rounded-xl">
						<div className="text-center mb-6">
							<div className="mt-4">
								<h2 className="text-xl font-semibold sm:text-2xl">{configData.song_name}</h2>
							</div>
						</div>
						<div className="text-center">
							{isWaveLoading && (
								<div className="text-center audio-loading">
									<div role="status">
										<svg
											aria-hidden="true"
											className="w-10 h-10 absolute inline text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
											viewBox="0 0 100 101"
											fill="none"
											xmlns="http://www.w3.org/2000/svg">
											<path
												d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
												fill="currentColor"
											/>
											<path
												d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
												fill="currentFill"
											/>
										</svg>
										<span className="sr-only">Loading...</span>
									</div>
								</div>
							)}
							<WavesurferPlayer
								height={50}
								barWidth={4}
								barRadius={6}
								barGap={2}
								cursorWidth={2}
								normalize={true}
								mediaControls={false}
								waveColor={configData.wave_color}
								progressColor="rgba(234, 178, 8, 0.87)"
								url={configData.audio_url}
								onReady={onReady}
								onPlay={() => setIsPlaying(true)}
								onPause={() => setIsPlaying(false)}
								sampleRate={48000}
							/>
							<div className="mt-3 p-4 ">
								<button
									className="inline-flex h-[45px] w-[45px] items-center justify-center rounded-full bg-gray-800 text-white drop-shadow-sm transition-colors duration-150 hover:bg-gray-200"
									onClick={onPlayPause}>
									{isPlaying ? (
										<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
											<path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
										</svg>
									) : (
										<svg viewBox="0 0 24 24" fill="currentColor" height="24" width="24">
											<path d="M8 5v14l11-7z" />
										</svg>
									)}
								</button>
							</div>
						</div>
						<div className="text-center">{configData.comments}</div>
					</div>
				</main>
				<script src="./assets/vendor/canvas-confetti/dist/confetti.browser.js"></script>
			</div>
		);
	}
};

export { SongShare };
